// Generated by tutorials/record.cjs from tutorials/articles/h43-diet-and-consistency-rules.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h43 --text-only` (wording) or
// `node tutorials/record.cjs h43` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'diet-and-consistency-rules',
  catalogId: 'h43',
  title: 'What each diet and texture gets',
  summary: 'Decide, row by row, what each diet type and each texture gets: the item, nothing, or a substitute. Then check the result in the Menu Preview before the kitchen sees it.',
  audience: 'Menu planners and dietitians who set up diets and textures on the menu',
  area: 'menus',
  learn: [
    'Stop serving a row to a diet, or serve it again',
    'Give a diet a substitute item',
    'Set what a texture gets: a modification, not allowed, or a substitute',
    'Check what each diet\'s tray gets in the Menu Preview',
    'Open a row\'s Item Details',
  ],
  keywords: [
    'diet',
    'diet type',
    'consistency',
    'texture',
    'pureed',
    'mechanical soft',
    'minced and moist',
    'thick liquids',
    'not served',
    'substitute',
    'override',
    'modification',
    'not allowed',
    'renal',
    'cardiac',
    'ccho',
    'low sodium',
    'finger foods',
    'menu preview',
    'item details',
    'menu cycle',
  ],
  steps: [
    {
      id: 'edit',
      part: 'Diet types',
      heading: 'Turn on editing',
      text: 'Go to the day and meal, then select Edit menu. Each diet column now changes when you click it.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/edit.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/edit.mp4',
          width: 1280,
          height: 842,
          durationSec: 3.9,
        },
        alt: 'Day 22 Afternoon with Editing beside the cycle name, and the diet type columns from Regular to Liberal Geriatric.',
      },
    },
    {
      id: 'not-served',
      part: 'Diet types',
      heading: 'Stop serving a row to a diet',
      text: 'Click the cell where the row meets the diet. The check mark becomes an X: that diet\'s trays no longer get the item. Click it again to serve it again.\n\nHere the chef salad comes off Renal trays.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/not-served.webp',
          width: 1280,
          height: 534,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/not-served.mp4',
          width: 1280,
          height: 534,
          durationSec: 4.2,
        },
        alt: 'The CHEF SALAD row with an X under Renal and check marks under every other diet type.',
      },
    },
    {
      id: 'cell-menu',
      part: 'Diet types',
      heading: 'Open a cell\'s options',
      text: 'Right-click a cell for its options. Override sets exactly what that diet gets; Clear takes the setting off; the Push commands copy the setting to the next cell.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/cell-menu.webp',
          width: 1092,
          height: 822,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/cell-menu.mp4',
          width: 1092,
          height: 822,
          durationSec: 3.7,
        },
        alt: 'The options of the MIXED VEGETABLES - Renal cell: Override, Clear, Push Left, Push Right, Push To, Push Up, Push Down and Item Type.',
      },
    },
    {
      id: 'override',
      part: 'Diet types',
      heading: 'Give a diet a substitute',
      text: 'Select Override. Under Item, type part of the substitute\'s name and pick it. Item Size takes its usual size; pick another if you need to.\n\nThe other fields are for less common cases: a different texture, item type or service item, or extra items that go with this diet\'s serving (Child Items).',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/override.webp',
          width: 1280,
          height: 754,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/override.mp4',
          width: 1280,
          height: 754,
          durationSec: 6,
        },
        alt: 'Menu Cycle Override - Renal - MIXED VEGETABLES, with GREEN BEANS picked as the item and Item Size 1/2 cup.',
      },
    },
    {
      id: 'override-saved',
      part: 'Diet types',
      heading: 'Save it',
      text: 'Select Save. The cell shows a diamond and the substitute\'s name: Renal trays now get green beans instead of the mixed vegetables.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/override-saved.webp',
          width: 1280,
          height: 700,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/override-saved.mp4',
          width: 1280,
          height: 700,
          durationSec: 6,
        },
        alt: 'The MIXED VEGETABLES row with a diamond and GREEN BEANS under Renal.',
      },
    },
    {
      id: 'check-diets',
      part: 'Diet types',
      heading: 'Check what each tray gets',
      text: 'Select the row. Menu Preview lists what each diet order gets: Renal now gets 1/2 cup GREEN BEANS, marked override. Check here before the kitchen works from the menu.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/check-diets.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/check-diets.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'MIXED VEGETABLES selected, with the Menu Preview showing GREEN BEANS for Renal and the mixed vegetables, made to each texture, for the textures.',
      },
    },
    {
      id: 'texture-click',
      part: 'Textures and liquids',
      heading: 'Set what a texture gets',
      text: 'Scroll to the consistency columns. Click a blank cell once for a modification (a wrench): that texture gets the item made to it, here the soup made Mechanical Soft. Click again for not allowed (an X), and again for blank.\n\nRight-click a consistency cell to pick Modification, Not allowed or Clear all directly.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/texture-click.webp',
          width: 1280,
          height: 916,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/texture-click.mp4',
          width: 1280,
          height: 916,
          durationSec: 5.3,
        },
        alt: 'The consistency columns, with a new wrench under Mechanical Soft on the SOUP CHICKEN NOODLE row, and wrenches and X marks on the other rows.',
      },
    },
    {
      id: 'texture-override',
      part: 'Textures and liquids',
      heading: 'Give a texture a substitute',
      text: 'When a texture should get something else, right-click its cell (a wrench), select Override and pick the item. Here Pureed trays get a ready-made pureed bread instead of the cornbread pureed in the kitchen.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/texture-override.webp',
          width: 1280,
          height: 754,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/texture-override.mp4',
          width: 1280,
          height: 754,
          durationSec: 6,
        },
        alt: 'Menu Cycle Override - Pureed - CORNBREAD, with BREAD PUREED picked as the item.',
      },
    },
    {
      id: 'check-texture',
      part: 'Textures and liquids',
      heading: 'Check the texture in the preview',
      text: 'Select Save, then select the row. In the Menu Preview, the Pureed diet order now gets BREAD PUREED, marked override, while Minced and Moist still gets the cornbread made to its texture.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/check-texture.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/check-texture.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'CORNBREAD with BREAD PUREED under Pureed, and the Menu Preview showing 1 ea BREAD PUREED for Regular, Pureed.',
      },
    },
    {
      id: 'details',
      part: 'Item details',
      heading: 'Open a row\'s Item Details',
      text: 'Select the pencil at the end of the row\'s name for Item Details. Details sets where the row shows: Do Not Show on General Menu, Do Not Show on Selective Menu or Selective Menu Only, and its service item and class. Facilities limits the row to some facilities, and Notes keeps a note with it.',
      media: {
        image: {
          src: '/help/diet-and-consistency-rules/details.webp',
          width: 612,
          height: 738,
        },
        clip: {
          src: '/help/diet-and-consistency-rules/details.mp4',
          width: 612,
          height: 738,
          durationSec: 2,
        },
        alt: 'Item Details for CORNBREAD on Day 22 Afternoon, BREAD: Service Item, TT MC Override, Class, the three menu check boxes and Modifier.',
      },
    },
  ],
  tips: [
    'Hover over a cell to see what that diet gets, in words, before you change it.',
    'Right-click a cell for Push Left, Push Right, Push Up and Push Down: they copy that cell\'s setting to the next cell, which is quicker than setting each one.',
    'Alt+Enter on a selected cell opens its Override window; Del clears it.',
    'On a tablet, tap a cell for its options: Served or Not served on a diet, Modification or Not allowed on a texture, Override, Clear and the Push commands. The tap itself changes nothing.',
    'Rules > AIM - Fill Item on MC sets a whole row from the item\'s Auto Item Mods (see Changing items and rows).',
  ],
  notes: [
    'Every change saves at once, while editing is on. Select Edit menu first.',
    'Taking a diet off a row that has a substitute also clears the substitute. You are asked first.',
    'A blank texture cell means no rule: that texture gets the row\'s item as it is. Make it a modification first if the texture needs something else.',
    'A row synced with rows on other days asks first whether to change the whole group (see Synced rows and Push To).',
  ],
  related: [
    'reading-a-menu-cycle',
    'changing-menu-items',
    'auto-item-mods',
    'synced-rows-and-push-to',
    'menu-analysis',
  ],
  recorded: '2026-10-26',
};

export default article;
